
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
  SendOutlined,
  SyncOutlined,
  StopOutlined,
  CopyOutlined,
  MailOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import faturaService from '../services/faturaService';
import { handleApiError } from '../utils/apiError';

const { Text } = Typography;

const STATUS_COLORS = {
  ABERTA: 'blue',
  PAGA: 'green',
  VENCIDA: 'red',
  CANCELADA: 'default',
};

const STATUS_CONSOLIDADO_COLORS = {
  ELEGIVEL_GERACAO: 'gold',
  GERADA_NAO_ENVIADA: 'blue',
  ENVIADA_AGUARDANDO: 'orange',
  VENCIDA: 'red',
  RECEBIDA: 'green',
  BLOQUEADA: 'default',
};

const ESTADO_FATURA_OPTIONS = [
  { value: 'GERADA_NAO_ENVIADA', label: 'GERADA_NAO_ENVIADA' },
  { value: 'ENVIADA_AGUARDANDO', label: 'ENVIADA_AGUARDANDO' },
  { value: 'VENCIDA', label: 'VENCIDA' },
  { value: 'PAGA', label: 'PAGA' },
  { value: 'CANCELADA', label: 'CANCELADA' },
  { value: 'SEM_COBRANCA_ATIVA', label: 'SEM_COBRANCA_ATIVA' },
];

const STATUS_ASAAS_COLORS = {
  PENDING: 'orange',
  OVERDUE: 'volcano',
  RECEIVED: 'green',
  CONFIRMED: 'green',
  CANCELED: 'default',
  CANCELLED: 'default',
  DELETED: 'default',
  REMOVIDA: 'default',
  NAO_ENVIADA: 'default',
};

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatCurrency = (value) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? moneyFormatter.format(numericValue) : moneyFormatter.format(0);
};

const formatDate = (value) => {
  if (!value) return '-';

  if (typeof value === 'string') {
    const plainDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (plainDateMatch) {
      const [, year, month, day] = plainDateMatch;
      return `${day}/${month}/${year}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('pt-BR');
};

const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizePhoneToWhatsApp = (phone) => {
  let normalized = String(phone || '').replace(/\D/g, '');
  normalized = normalized.replace(/^0+/, '');
  if (!normalized) return '';
  if (!normalized.startsWith('55')) {
    normalized = `55${normalized}`;
  }
  return normalized;
};

const toComparableText = (value) => String(value || '').trim().toLowerCase();

const compareText = (a, b) =>
  toComparableText(a).localeCompare(toComparableText(b), 'pt-BR', { sensitivity: 'base' });

const toDateSortValue = (value) => {
  if (!value) return 0;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day).getTime();
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const getDescricaoParcelaExibicao = (parcela) =>
  (parcela?.descricao_exibicao || '').trim() ||
  (parcela?.descricao_turma || '').trim() ||
  (parcela?.descricao || '').trim() ||
  'Mensalidade';

const montarDescricaoCobranca = (fatura) => {
  const competencia = fatura?.competencia || '-';
  const parcelas = Array.isArray(fatura?.parcelas) ? fatura.parcelas : [];

  const linhasItens = parcelas.map((parcela) => {
    const nomeItem = getDescricaoParcelaExibicao(parcela);
    const valorItem =
      typeof parcela?.valor_com_desconto === 'number'
        ? parcela.valor_com_desconto
        : typeof parcela?.valor === 'number'
        ? parcela.valor
        : 0;

    return `- ${nomeItem}: ${formatCurrency(valorItem)}`;
  });

  if (linhasItens.length === 0) {
    const nomeItemFallback = (fatura?.turma_nome || '').trim() || 'Mensalidade';
    const valorFallback =
      typeof fatura?.valor_total_com_desconto === 'number'
        ? fatura.valor_total_com_desconto
        : typeof fatura?.valor_total === 'number'
        ? fatura.valor_total
        : 0;

    return `Fatura ${competencia}\nItens:\n- ${nomeItemFallback}: ${formatCurrency(valorFallback)}`;
  }

  return `Fatura ${competencia}\nItens:\n${linhasItens.join('\n')}`;
};

const calcularTotaisFatura = (fatura) => {
  const valorTotalSemDesconto = Number(fatura?.valor_total_sem_desconto);
  const valorTotalComDesconto = Number(fatura?.valor_total_com_desconto);
  const valorTotalLegacy = Number(fatura?.valor_total);

  if (Number.isFinite(valorTotalSemDesconto) && Number.isFinite(valorTotalComDesconto)) {
    return {
      valor_total_sem_desconto: valorTotalSemDesconto,
      valor_total_com_desconto: valorTotalComDesconto,
    };
  }

  if (Array.isArray(fatura?.parcelas) && fatura.parcelas.length > 0) {
    const totaisParcelas = fatura.parcelas.reduce(
      (acc, parcela) => {
        const valorBase = Number(parcela?.valor);
        const valorComDesconto = Number(parcela?.valor_com_desconto);

        if (Number.isFinite(valorBase)) {
          acc.valor_total_sem_desconto += valorBase;
        }

        if (Number.isFinite(valorComDesconto)) {
          acc.valor_total_com_desconto += valorComDesconto;
        } else if (Number.isFinite(valorBase)) {
          acc.valor_total_com_desconto += valorBase;
        }

        return acc;
      },
      {
        valor_total_sem_desconto: 0,
        valor_total_com_desconto: 0,
      }
    );

    return totaisParcelas;
  }

  const valorFallback = Number.isFinite(valorTotalLegacy) ? valorTotalLegacy : 0;
  return {
    valor_total_sem_desconto: valorFallback,
    valor_total_com_desconto: valorFallback,
  };
};

const montarDadosCompartilhamentoFatura = (fatura) => {
  const totais = calcularTotaisFatura(fatura);
  const linhas = [
    `Olá, sua fatura chegou!`,
    `Caso já tenha efetuado o pagamento, favor desconsiderar esta mensagem!`,
    `Obrigada`,
    '', // linha em branco pra separar visualmente
    `Fatura ${fatura?.competencia || '-'}`,
    `Aluno: ${fatura?.nome_aluno || '-'}`,
    `Responsável: ${fatura?.responsavel_nome || '-'}`,
    `Vencimento: ${formatDate(fatura?.data_vencimento)}`,
    `Valor total (sem desconto): ${formatCurrency(totais.valor_total_sem_desconto)}`,
    `Valor total (com desconto): ${formatCurrency(totais.valor_total_com_desconto)}`,
  ];

  if (fatura?.invoice_url) {
    linhas.push(`Link da fatura: ${fatura.invoice_url}`);
  }
  if (fatura?.bank_slip_url) {
    linhas.push(`Boleto: ${fatura.bank_slip_url}`);
  }
  if (fatura?.pix_copy_paste) {
    linhas.push('PIX copia e cola:');
    linhas.push(fatura.pix_copy_paste);
  }

  return linhas.join('\n');
};

const FaturasList = ({ pageMode = 'all' }) => {
  const isOperacionalPage = pageMode === 'operacional';
  const isGestaoPage = pageMode === 'gestao';
  const DEFAULT_GESTAO_SORT_BY = 'nome_aluno';
  const DEFAULT_GESTAO_SORT_ORDER = 'asc';
  const MODO_SELECAO = {
    GERACAO: 'GERACAO',
    ENVIO_ASAAS: 'ENVIO_ASAAS',
  };

  const [faturas, setFaturas] = useState([]);
  const [competenciaOperacional, setCompetenciaOperacional] = useState(dayjs());
  const [operacionalRows, setOperacionalRows] = useState([]);
  const [operacionalLoading, setOperacionalLoading] = useState(false);
  const [gerandoLote, setGerandoLote] = useState(false);
  const [enviandoLoteAsaas, setEnviandoLoteAsaas] = useState(false);
  const [operacionalAcaoKey, setOperacionalAcaoKey] = useState(null);
  const [modoSelecaoLote, setModoSelecaoLote] = useState(MODO_SELECAO.GERACAO);
  const [selectedOperacionalRowKeys, setSelectedOperacionalRowKeys] = useState([]);
  const [selectedOperacionalRows, setSelectedOperacionalRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acaoId, setAcaoId] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [detalheFatura, setDetalheFatura] = useState(null);
  const [filtroAluno, setFiltroAluno] = useState(undefined);
  const [filtroResponsavel, setFiltroResponsavel] = useState(undefined);
  const [filtroEstadoFatura, setFiltroEstadoFatura] = useState(undefined);
  const [filtroPertoVencimento, setFiltroPertoVencimento] = useState(false);
  const [filtroVenceHoje, setFiltroVenceHoje] = useState(false);
  const [filtroVencidasPendentes, setFiltroVencidasPendentes] = useState(false);
  const [filtroVencimentoRange, setFiltroVencimentoRange] = useState(null);
  const [selectedFaturasRowKeys, setSelectedFaturasRowKeys] = useState([]);
  const [atualizandoStatusLote, setAtualizandoStatusLote] = useState(false);
  const [gestaoPage, setGestaoPage] = useState(1);
  const [gestaoPageSize, setGestaoPageSize] = useState(10);
  const [gestaoTotal, setGestaoTotal] = useState(0);
  const [gestaoSortBy, setGestaoSortBy] = useState(DEFAULT_GESTAO_SORT_BY);
  const [gestaoSortOrder, setGestaoSortOrder] = useState(DEFAULT_GESTAO_SORT_ORDER);
  const [filtroAlunoInput, setFiltroAlunoInput] = useState('');
  const [filtroResponsavelInput, setFiltroResponsavelInput] = useState('');
  const [alertasOperacionais, setAlertasOperacionais] = useState({
    perto_vencimento: 0,
    vence_hoje: 0,
    vencidas: 0,
  });

  const fetchFaturas = async (
    page = gestaoPage,
    pageSize = gestaoPageSize,
    filtrosOverride = {},
    ordenacaoOverride = {}
  ) => {
    setLoading(true);
    try {
      const filtros = {
        filtro_aluno: filtrosOverride.filtro_aluno ?? filtroAluno,
        filtro_responsavel: filtrosOverride.filtro_responsavel ?? filtroResponsavel,
        filtro_estado_fatura: filtrosOverride.filtro_estado_fatura ?? filtroEstadoFatura,
        perto_vencimento: filtrosOverride.perto_vencimento ?? filtroPertoVencimento,
        vence_hoje: filtrosOverride.vence_hoje ?? filtroVenceHoje,
        vencidas_pendentes: filtrosOverride.vencidas_pendentes ?? filtroVencidasPendentes,
        data_vencimento_inicio: filtrosOverride.data_vencimento_inicio,
        data_vencimento_fim: filtrosOverride.data_vencimento_fim,
      };
      const rangeAtual = Array.isArray(filtroVencimentoRange) ? filtroVencimentoRange : null;
      if (filtros.data_vencimento_inicio === undefined) {
        filtros.data_vencimento_inicio = rangeAtual?.[0]?.format?.('YYYY-MM-DD') || undefined;
      }
      if (filtros.data_vencimento_fim === undefined) {
        filtros.data_vencimento_fim = rangeAtual?.[1]?.format?.('YYYY-MM-DD') || undefined;
      }
      const sortBy = ordenacaoOverride.sort_by ?? gestaoSortBy;
      const sortOrder = ordenacaoOverride.sort_order ?? gestaoSortOrder;

      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (filtros.filtro_aluno) {
        params.filtro_aluno = filtros.filtro_aluno;
      }
      if (filtros.filtro_responsavel) {
        params.filtro_responsavel = filtros.filtro_responsavel;
      }
      if (filtros.filtro_estado_fatura) {
        params.filtro_estado_fatura = filtros.filtro_estado_fatura;
      }
      if (filtros.perto_vencimento) {
        params.perto_vencimento = true;
      }
      if (filtros.vence_hoje) {
        params.vence_hoje = true;
      }
      if (filtros.vencidas_pendentes) {
        params.vencidas_pendentes = true;
      }
      if (filtros.data_vencimento_inicio) {
        params.data_vencimento_inicio = filtros.data_vencimento_inicio;
      }
      if (filtros.data_vencimento_fim) {
        params.data_vencimento_fim = filtros.data_vencimento_fim;
      }

      const response = await faturaService.list(params);
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      const pagination = response.data?.pagination || {};
      const resumoAlertas = response.data?.resumo_alertas || {};
      const faturasNormalizadas = items.map((fatura) => ({
        ...fatura,
        ...calcularTotaisFatura(fatura),
      }));
      setFaturas(faturasNormalizadas);
      setAlertasOperacionais({
        perto_vencimento: Number(resumoAlertas.perto_vencimento) || 0,
        vence_hoje: Number(resumoAlertas.vence_hoje) || 0,
        vencidas: Number(resumoAlertas.vencidas) || 0,
      });
      setGestaoPage(Number(pagination.page) || page);
      setGestaoPageSize(Number(pagination.page_size) || pageSize);
      setGestaoTotal(Number(pagination.total) || 0);
      setGestaoSortBy(sortBy);
      setGestaoSortOrder(sortOrder);
      setFiltroPertoVencimento(Boolean(filtros.perto_vencimento));
      setFiltroVenceHoje(Boolean(filtros.vence_hoje));
      setFiltroVencidasPendentes(Boolean(filtros.vencidas_pendentes));
      setSelectedFaturasRowKeys([]);
    } catch (error) {
      handleApiError(error, 'Erro ao buscar faturas');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperacional = async (compDate = competenciaOperacional) => {
    if (!compDate) {
      setOperacionalRows([]);
      setSelectedOperacionalRowKeys([]);
      setSelectedOperacionalRows([]);
      return;
    }

    setOperacionalLoading(true);
    try {
      const competencia = compDate.format('YYYY-MM');
      const response = await faturaService.listOperacional(competencia);
      setOperacionalRows(Array.isArray(response.data) ? response.data : []);
      setSelectedOperacionalRowKeys([]);
      setSelectedOperacionalRows([]);
    } catch (error) {
      handleApiError(error, 'Erro ao buscar visão operacional de faturas');
    } finally {
      setOperacionalLoading(false);
    }
  };

  useEffect(() => {
    if (!isGestaoPage) {
      fetchOperacional(dayjs());
    }
    if (!isOperacionalPage) {
      fetchFaturas();
    }
  }, [isGestaoPage, isOperacionalPage]);

  const refreshOperacionalContext = async () => {
    if (!isGestaoPage) {
      await fetchOperacional(competenciaOperacional);
    }
  };

  const refreshGestaoContext = async () => {
    if (!isOperacionalPage) {
      await fetchFaturas(gestaoPage, gestaoPageSize);
    }
  };

  const aplicarFiltroAlerta = (tipo) => {
    const filtrosAlerta = {
      perto_vencimento: tipo === 'perto_vencimento',
      vence_hoje: tipo === 'vence_hoje',
      vencidas_pendentes: tipo === 'vencidas_pendentes',
    };

    setFiltroEstadoFatura(undefined);
    fetchFaturas(1, gestaoPageSize, {
      filtro_estado_fatura: undefined,
      ...filtrosAlerta,
    });
  };

  const filtrarPertoVencimento = () => aplicarFiltroAlerta('perto_vencimento');
  const filtrarVencemHoje = () => aplicarFiltroAlerta('vence_hoje');
  const filtrarSomenteVencidasPendentes = () => aplicarFiltroAlerta('vencidas_pendentes');

  const limparFiltrosAlerta = () => {
    fetchFaturas(1, gestaoPageSize, {
      perto_vencimento: false,
      vence_hoje: false,
      vencidas_pendentes: false,
    });
  };

  const possuiFiltroAlertaAtivo =
    filtroPertoVencimento || filtroVenceHoje || filtroVencidasPendentes;
  const filtroAlertaAtivoLabel = filtroPertoVencimento
    ? 'Perto do vencimento'
    : filtroVenceHoje
    ? 'Vencem hoje'
    : filtroVencidasPendentes
    ? 'Vencidas pendentes'
    : null;

  const mostrarRetornoAsaas = (result) => {
    const invoiceUrl = result?.invoice_url;
    const bankSlipUrl = result?.bank_slip_url;
    if (!invoiceUrl && !bankSlipUrl) return;

    Modal.info({
      title: 'Links da cobrança ASAAS',
      width: 640,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {invoiceUrl ? (
            <a href={invoiceUrl} target="_blank" rel="noreferrer">
              Abrir invoice_url
            </a>
          ) : null}
          {bankSlipUrl ? (
            <a href={bankSlipUrl} target="_blank" rel="noreferrer">
              Abrir bank_slip_url
            </a>
          ) : null}
        </Space>
      ),
    });
  };

  const abrirDetalhes = async (id) => {
    setDetalheOpen(true);
    setDetalheLoading(true);
    setDetalheFatura(null);
    try {
      const response = await faturaService.getById(id);
      const detalhe = response.data || null;
      setDetalheFatura(
        detalhe
          ? {
              ...detalhe,
              ...calcularTotaisFatura(detalhe),
            }
          : null
      );
    } catch (error) {
      handleApiError(error, 'Erro ao buscar detalhes da fatura');
    } finally {
      setDetalheLoading(false);
    }
  };


  const onGerarLote = async () => {
    if (selectedOperacionalRows.length === 0) {
      return;
    }

    const itens = selectedOperacionalRows
      .filter((row) => row.is_elegivel_geracao)
      .map((row) => ({
        id_aluno: row.id_aluno,
        competencia: row.competencia,
      }));

    if (itens.length === 0) {
      message.warning('Nenhuma linha elegível para gerar fatura em lote');
      return;
    }

    setGerandoLote(true);
    try {
      const response = await faturaService.gerarLote({ itens });
      const data = response.data || {};
      Modal.info({
        title: 'Resultado da geração em lote',
        content: (
          <Space direction="vertical">
            <span>Total processado: {data.total ?? 0}</span>
            <span>Sucessos: {data.sucessos ?? 0}</span>
            <span>Falhas: {data.falhas ?? 0}</span>
          </Space>
        ),
      });
      await refreshOperacionalContext();
      await refreshGestaoContext();
    } catch (error) {
      handleApiError(error, 'Erro ao gerar faturas em lote');
    } finally {
      setGerandoLote(false);
    }
  };

  const abrirModalResultadoEnvioLote = (data, itensSelecionados) => {
    const resultados = Array.isArray(data?.resultados) ? data.resultados : [];
    const itensPorChave = new Map(
      (itensSelecionados || []).map((row) => [
        `${row.id_aluno}-${row.competencia}`,
        row,
      ])
    );

    const resultadoDataSource = resultados.map((item, index) => {
      const chave = `${item.id_aluno}-${item.competencia}`;
      const origem = itensPorChave.get(chave);
      return {
        key: `${chave}-${index}`,
        aluno: origem?.nome_aluno || `ID ${item.id_aluno}`,
        competencia: item.competencia || '-',
        sucesso: Boolean(item.sucesso),
        mensagem: item.mensagem || '-',
      };
    });

    Modal.info({
      title: 'Resultado do envio em lote ao ASAAS',
      width: 900,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <span>Total processado: {data?.total ?? 0}</span>
            <span>Sucessos: {data?.sucessos ?? 0}</span>
            <span>Falhas: {data?.falhas ?? 0}</span>
          </Space>
          <Table
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            dataSource={resultadoDataSource}
            columns={[
              {
                title: 'Aluno',
                dataIndex: 'aluno',
                key: 'aluno',
              },
              {
                title: 'Competência',
                dataIndex: 'competencia',
                key: 'competencia',
                width: 130,
                align: 'center',
              },
              {
                title: 'Resultado',
                dataIndex: 'sucesso',
                key: 'sucesso',
                width: 120,
                align: 'center',
                render: (sucesso) => (
                  <Tag color={sucesso ? 'green' : 'red'}>{sucesso ? 'SUCESSO' : 'FALHA'}</Tag>
                ),
              },
              {
                title: 'Mensagem',
                dataIndex: 'mensagem',
                key: 'mensagem',
              },
            ]}
            locale={{ emptyText: 'Sem resultados detalhados' }}
          />
        </Space>
      ),
    });
  };

  const onEnviarAsaasLote = async () => {
    if (selectedOperacionalRows.length === 0) {
      return;
    }

    const itens = selectedOperacionalRows
      .filter((row) => row.is_elegivel_envio_asaas)
      .map((row) => ({
        id_aluno: row.id_aluno,
        competencia: row.competencia,
      }));

    if (itens.length === 0) {
      message.warning('Nenhuma linha elegível para envio em lote ao ASAAS');
      return;
    }

    const linhasSelecionadas = [...selectedOperacionalRows];

    Modal.confirm({
      title: `Deseja enviar ${itens.length} faturas selecionadas ao Asaas?`,
      content: 'Essa ação criará cobranças externas para as faturas elegíveis.',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setEnviandoLoteAsaas(true);
        try {
          const response = await faturaService.enviarLoteAsaas({ itens });
          const data = response.data || {};
          abrirModalResultadoEnvioLote(data, linhasSelecionadas);
          await refreshOperacionalContext();
          await refreshGestaoContext();
        } catch (error) {
          handleApiError(error, 'Erro ao enviar faturas em lote ao ASAAS');
        } finally {
          setEnviandoLoteAsaas(false);
        }
      },
    });
  };

  const getOperacionalRowKey = (record) => `${record.id_aluno}-${record.competencia}`;

  const sincronizarAsaasLinha = async (record) => {
    const rowKey = getOperacionalRowKey(record);
    setOperacionalAcaoKey(rowKey);
    try {
      const response = await faturaService.sincronizarAsaas({
        itens: [
          {
            id_aluno: record.id_aluno,
            competencia: record.competencia,
          },
        ],
      });
      const item = response.data?.resultados?.[0];
      if (item?.sucesso) {
        message.success(item.mensagem || 'Status sincronizado com o ASAAS');
      } else {
        message.warning(item?.mensagem || 'Falha ao sincronizar no ASAAS');
      }
      await refreshOperacionalContext();
      await refreshGestaoContext();
    } catch (error) {
      handleApiError(error, 'Erro ao sincronizar status no ASAAS');
    } finally {
      setOperacionalAcaoKey(null);
    }
  };

  const sincronizarAsaasFatura = async (record) => {
    setAcaoId(record.id);
    try {
      const response = await faturaService.sincronizarAsaas({
        itens: [
          {
            id_aluno: record.id_aluno,
            competencia: record.competencia,
          },
        ],
      });
      const item = response.data?.resultados?.[0];
      if (item?.sucesso) {
        message.success(item.mensagem || 'Status sincronizado com o ASAAS');
      } else {
        message.warning(item?.mensagem || 'Falha ao sincronizar no ASAAS');
      }
      await refreshOperacionalContext();
      await refreshGestaoContext();
    } catch (error) {
      handleApiError(error, 'Erro ao sincronizar status no ASAAS');
    } finally {
      setAcaoId(null);
    }
  };

  const abrirModalResultadoAtualizacaoStatusLote = (data) => {
    const details = Array.isArray(data?.details) ? data.details : [];

    Modal.info({
      title: 'Resultado da atualização de status ASAAS',
      width: 900,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <span>Total processado: {data?.total ?? 0}</span>
            <span>Atualizados: {data?.updated ?? 0}</span>
            <span>Sem mudança: {data?.unchanged ?? 0}</span>
            <span>Ignorados: {data?.ignored ?? 0}</span>
            <span>Erros: {data?.error ?? 0}</span>
          </Space>
          <Table
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            dataSource={details.map((item, index) => ({
              key: `${item?.fatura_id ?? 'sem-id'}-${index}`,
              fatura_id: item?.fatura_id ?? '-',
              status: item?.status ?? '-',
              reason: item?.reason || '-',
            }))}
            columns={[
              {
                title: 'Fatura',
                dataIndex: 'fatura_id',
                key: 'fatura_id',
                width: 120,
                align: 'center',
              },
              {
                title: 'Resultado',
                dataIndex: 'status',
                key: 'status',
                width: 140,
                align: 'center',
                render: (status) => {
                  const colorMap = {
                    updated: 'green',
                    unchanged: 'blue',
                    ignored: 'gold',
                    error: 'red',
                  };
                  return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
                },
              },
              {
                title: 'Motivo',
                dataIndex: 'reason',
                key: 'reason',
              },
            ]}
            locale={{ emptyText: 'Sem detalhes' }}
          />
        </Space>
      ),
    });
  };

  const onAtualizarStatusAsaasSelecionadas = async () => {
    if (selectedFaturasRowKeys.length === 0) {
      message.warning('Selecione ao menos uma fatura para atualizar');
      return;
    }

    setAtualizandoStatusLote(true);
    try {
      const response = await faturaService.atualizarStatusLote({
        fatura_ids: selectedFaturasRowKeys.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0),
      });
      const data = response.data || {};
      abrirModalResultadoAtualizacaoStatusLote(data);
      await refreshOperacionalContext();
      await refreshGestaoContext();
    } catch (error) {
      handleApiError(error, 'Erro ao atualizar status ASAAS em lote');
    } finally {
      setAtualizandoStatusLote(false);
    }
  };

  const cancelarAsaasFatura = (record) => {
    Modal.confirm({
      title: `Cancelar cobrança ASAAS da fatura ${record.id}?`,
      content: 'Essa ação cancela a cobrança remota e mantém o histórico local.',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setAcaoId(record.id);
        try {
          const response = await faturaService.cancelarAsaas({
            id_aluno: record.id_aluno,
            competencia: record.competencia,
          });
          message.success(response.data?.mensagem || 'Cobrança cancelada no ASAAS');
          await refreshOperacionalContext();
          await refreshGestaoContext();
        } catch (error) {
          handleApiError(error, 'Erro ao cancelar cobrança no ASAAS');
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const excluirAsaasFatura = (record) => {
    Modal.confirm({
      title: `Excluir cobrança ASAAS da fatura ${record.id}?`,
      content: 'Essa ação remove a cobrança remota e preserva o histórico local.',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setAcaoId(record.id);
        try {
          const response = await faturaService.excluirAsaas({
            id_aluno: record.id_aluno,
            competencia: record.competencia,
          });
          message.success(response.data?.mensagem || 'Cobrança excluída no ASAAS');
          await refreshOperacionalContext();
          await refreshGestaoContext();
        } catch (error) {
          handleApiError(error, 'Erro ao excluir cobrança no ASAAS');
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const excluirFaturaLocal = (record) => {
    if (!record?.pode_excluir_local) {
      message.warning(
        record?.motivo_bloqueio_exclusao_local ||
          'Existe cobrança ativa no ASAAS. Cancele/exclua no ASAAS ou sincronize antes de excluir localmente.'
      );
      return;
    }

    Modal.confirm({
      title: `Excluir fatura local ${record.id}?`,
      content:
        'Essa ação remove apenas a fatura local, preserva histórico financeiro e libera as parcelas elegíveis para nova geração.',
      okText: 'Excluir local',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setAcaoId(record.id);
        try {
          const response = await faturaService.excluirLocal({
            id_aluno: record.id_aluno,
            competencia: record.competencia,
          });
          message.success(response.data?.message || 'Fatura local excluída com sucesso');
          await refreshOperacionalContext();
          await refreshGestaoContext();
        } catch (error) {
          handleApiError(error, 'Erro ao excluir fatura local');
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const reenviarFaturaAsaas = (record) => {
    Modal.confirm({
      title: `Reenviar fatura ${record.id} ao ASAAS?`,
      content: 'A ação cria uma nova cobrança remota quando não há cobrança ativa.',
      okText: 'Reenviar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setAcaoId(record.id);
        try {
          const response = await faturaService.enviarAsaas(record.id);
          const result = response.data || {};
          message.success(`Fatura ${record.id} enviada ao ASAAS com sucesso`);
          mostrarRetornoAsaas(result);
          await refreshOperacionalContext();
          await refreshGestaoContext();
        } catch (error) {
          handleApiError(error, 'Erro ao reenviar fatura ao ASAAS');
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const copiarDadosFatura = async (record) => {
    const texto = montarDadosCompartilhamentoFatura(record);
    if (!navigator?.clipboard) {
      message.warning('Seu navegador não suporta cópia automática.');
      return;
    }
    try {
      await navigator.clipboard.writeText(texto);
      message.success('Dados da fatura copiados.');
    } catch (error) {
      handleApiError(error, 'Erro ao copiar dados da fatura');
    }
  };

  const enviarFaturaParaCliente = (record) => {
    const mensagem = montarDadosCompartilhamentoFatura(record);
    const phone = normalizePhoneToWhatsApp(record?.responsavel_telefone);

    if (phone) {
      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(mensagem)}`;
      const openedWindow = window.open(whatsappUrl, '_blank', 'noopener');
      if (!openedWindow) {
        message.warning('Não foi possível abrir o WhatsApp. Verifique bloqueio de pop-up.');
      }
      return;
    }

    if (record?.responsavel_email) {
      const subject = encodeURIComponent(`Fatura ${record?.competencia || ''}`.trim());
      const body = encodeURIComponent(mensagem);
      window.location.href = `mailto:${record.responsavel_email}?subject=${subject}&body=${body}`;
      return;
    }

    if (record?.invoice_url || record?.bank_slip_url) {
      window.open(record.invoice_url || record.bank_slip_url, '_blank', 'noopener');
      return;
    }

    message.warning('Não há dados de contato ou link de cobrança disponível para esta fatura.');
  };

  const operacionalColumns = [
      {
        title: 'Aluno',
        key: 'aluno',
        render: (_, record) => record.nome_aluno || record.id_aluno,
      },
      {
        title: 'Responsável',
        dataIndex: 'responsavel_nome',
        key: 'responsavel_nome',
        render: (value) => value || '-',
      },
      {
        title: 'Competência',
        dataIndex: 'competencia',
        key: 'competencia',
        width: 120,
        align: 'center',
      },
      {
        title: 'Valor Total',
        dataIndex: 'valor_total_competencia',
        key: 'valor_total_competencia',
        render: formatCurrency,
        width: 140,
        align: 'right',
      },
      {
        title: 'Valor Elegível',
        dataIndex: 'valor_total_elegivel',
        key: 'valor_total_elegivel',
        render: formatCurrency,
        width: 140,
        align: 'right',
      },
      {
        title: 'Parcelas',
        key: 'parcelas',
        width: 120,
        align: 'center',
        render: (_, record) => `${record.qtd_parcelas_elegiveis}/${record.qtd_parcelas_total}`,
      },
      {
        title: 'Fatura',
        dataIndex: 'status_fatura',
        key: 'status_fatura',
        width: 150,
        align: 'center',
        render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status || '-'}</Tag>,
      },
      {
        title: 'Asaas',
        dataIndex: 'status_asaas',
        key: 'status_asaas',
        width: 150,
        align: 'center',
        render: (status) => status || '-',
      },
      {
        title: 'Consolidado',
        dataIndex: 'status_consolidado',
        key: 'status_consolidado',
        width: 170,
        align: 'center',
        render: (status) => (
          <Tag color={STATUS_CONSOLIDADO_COLORS[status] || 'default'}>{status || '-'}</Tag>
        ),
      },
      {
        title: 'Ações Asaas',
        key: 'acoes_asaas',
        width: 90,
        align: 'center',
        render: (_, record) => {
          if (!record.has_cobranca_historica) {
            return '-';
          }

          const rowKey = getOperacionalRowKey(record);
          const isLoading = operacionalAcaoKey === rowKey;

          return (
            <Space>
              <Tooltip title="Sincronizar">
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={() => sincronizarAsaasLinha(record)}
                  loading={isLoading}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ];

  const operacionalRowSelection = {
    selectedRowKeys: selectedOperacionalRowKeys,
    onChange: (keys, rows) => {
      setSelectedOperacionalRowKeys(keys);
      setSelectedOperacionalRows(rows);
    },
    getCheckboxProps: (record) => ({
      disabled:
        modoSelecaoLote === MODO_SELECAO.GERACAO
          ? record.status_consolidado !== 'ELEGIVEL_GERACAO'
          : record.status_consolidado !== 'GERADA_NAO_ENVIADA',
    }),
  };

  const selectedElegiveisGeracao = selectedOperacionalRows.filter(
    (row) => row.is_elegivel_geracao
  ).length;

  const selectedElegiveisEnvioAsaas = selectedOperacionalRows.filter(
    (row) => row.is_elegivel_envio_asaas
  ).length;

  const faturasSemCobranca = faturas.filter(
    (fatura) => fatura?.estado_fatura === 'SEM_COBRANCA_ATIVA'
  ).length;

  const detalheTotais = useMemo(
    () => calcularTotaisFatura(detalheFatura),
    [detalheFatura]
  );

  const faturasRowSelection = {
    selectedRowKeys: selectedFaturasRowKeys,
    onChange: (keys) => {
      setSelectedFaturasRowKeys(keys);
    },
  };

  const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        align: 'center',
        sorter: (a, b) => Number(a?.id || 0) - Number(b?.id || 0),
      },
      {
        title: 'Aluno',
        dataIndex: 'nome_aluno',
        key: 'aluno',
        render: (nome_aluno, record) => nome_aluno || record.id_aluno,
        sorter: true,
        sortOrder:
          gestaoSortBy === 'nome_aluno'
            ? gestaoSortOrder === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
      },
      {
        title: 'Turma',
        dataIndex: 'turma_nome',
        key: 'turma',
        render: (turma_nome) => turma_nome || '-',
        width: 120,
        align: 'center',
        sorter: (a, b) => compareText(a?.turma_nome || '-', b?.turma_nome || '-'),
      },
      {
        title: 'Responsável',
        dataIndex: 'responsavel_nome',
        key: 'responsavel',
        render: (responsavel_nome) => responsavel_nome || '-',
        width: 150,
        align: 'center',
        sorter: (a, b) => compareText(a?.responsavel_nome || '-', b?.responsavel_nome || '-'),
      },
      {
        title: 'Competência',
        dataIndex: 'competencia',
        key: 'competencia',
        width: 120,
        align: 'center',
        sorter: (a, b) => compareText(a?.competencia || '-', b?.competencia || '-'),
      },
      {
        title: 'Valor Total (sem desconto)',
        dataIndex: 'valor_total_sem_desconto',
        key: 'valor_total_sem_desconto',
        render: formatCurrency,
        width: 160,
        align: 'right',
        sorter: (a, b) =>
          toFiniteNumber(a?.valor_total_sem_desconto) - toFiniteNumber(b?.valor_total_sem_desconto),
      },
      {
        title: 'Valor Total (com desconto)',
        dataIndex: 'valor_total_com_desconto',
        key: 'valor_total_com_desconto',
        render: formatCurrency,
        width: 160,
        align: 'right',
        sorter: (a, b) =>
          toFiniteNumber(a?.valor_total_com_desconto) - toFiniteNumber(b?.valor_total_com_desconto),
      },
      {
        title: 'Estado Fatura',
        dataIndex: 'estado_fatura',
        key: 'estado_fatura',
        width: 170,
        align: 'center',
        render: (estado) => (
          <Tag color={STATUS_CONSOLIDADO_COLORS[estado] || 'default'}>{estado || '-'}</Tag>
        ),
        sorter: true,
        sortOrder:
          gestaoSortBy === 'estado_fatura'
            ? gestaoSortOrder === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
      },
      {
        title: 'Status ASAAS',
        dataIndex: 'status_asaas',
        key: 'status_asaas',
        width: 150,
        align: 'center',
        render: (status) => (
          <Tag color={STATUS_ASAAS_COLORS[status] || 'default'}>{status || '-'}</Tag>
        ),
        sorter: true,
        sortOrder:
          gestaoSortBy === 'status_asaas'
            ? gestaoSortOrder === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
      },
      {
        title: 'Cobrança Ativa',
        dataIndex: 'has_cobranca_ativa',
        key: 'has_cobranca_ativa',
        width: 130,
        align: 'center',
        render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? 'SIM' : 'NÃO'}</Tag>,
        sorter: (a, b) => Number(Boolean(a?.has_cobranca_ativa)) - Number(Boolean(b?.has_cobranca_ativa)),
      },
      {
        title: 'Parcelas',
        dataIndex: 'quantidade_parcelas',
        key: 'quantidade_parcelas',
        width: 100,
        align: 'center',
        sorter: (a, b) => Number(a?.quantidade_parcelas || 0) - Number(b?.quantidade_parcelas || 0),
      },
      {
        title: 'Vencimento',
        dataIndex: 'data_vencimento',
        key: 'vencimento',
        render: formatDate,
        width: 120,
        align: 'center',
        sorter: true,
        sortOrder:
          gestaoSortBy === 'data_vencimento'
            ? gestaoSortOrder === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
      },
      {
        title: 'Ações',
        key: 'acoes',
        width: 320,
        align: 'center',
        render: (_, record) => {
          const podeCancelar = Boolean(record.has_cobranca_ativa);
          const podeExcluir = Boolean(record.has_cobranca_ativa);
          const podeReenviar =
            (record.status === 'ABERTA') &&
            (!record.has_cobranca_ativa || record.status_asaas === 'REMOVIDA');
          const podeExcluirLocal = Boolean(record.pode_excluir_local);
          const motivoBloqueioExcluirLocal =
            record.motivo_bloqueio_exclusao_local ||
            'Existe cobrança ativa no ASAAS. Cancele/exclua no ASAAS ou sincronize antes de excluir localmente.';

          return (
            <Space>
              <Tooltip title="Visualizar">
                <Button
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() => abrirDetalhes(record.id)}
                />
              </Tooltip>

              <Tooltip title="Sincronizar">
                <Button
                  icon={<SyncOutlined />}
                  size="small"
                  onClick={() => sincronizarAsaasFatura(record)}
                  loading={acaoId === record.id}
                />
              </Tooltip>

              <Tooltip title="Cancelar Asaas">
                <span>
                  <Button
                    icon={<StopOutlined />}
                    size="small"
                    onClick={() => cancelarAsaasFatura(record)}
                    disabled={!podeCancelar}
                    loading={acaoId === record.id}
                  />
                </span>
              </Tooltip>

              <Tooltip title="Excluir Asaas">
                <span>
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    onClick={() => excluirAsaasFatura(record)}
                    disabled={!podeExcluir}
                    loading={acaoId === record.id}
                  />
                </span>
              </Tooltip>

              <Tooltip title="Reenviar">
                <span>
                  <Button
                    icon={<SendOutlined />}
                    size="small"
                    type="primary"
                    onClick={() => reenviarFaturaAsaas(record)}
                    disabled={!podeReenviar}
                    loading={acaoId === record.id}
                  />
                </span>
              </Tooltip>

              <Tooltip title="Enviar fatura para cliente">
                <Button
                  icon={<MailOutlined />}
                  size="small"
                  onClick={() => enviarFaturaParaCliente(record)}
                />
              </Tooltip>

              <Tooltip title="Copiar dados da Fatura">
                <Button
                  icon={<CopyOutlined />}
                  size="small"
                  onClick={() => copiarDadosFatura(record)}
                />
              </Tooltip>

              <Tooltip title={podeExcluirLocal ? 'Excluir fatura local' : motivoBloqueioExcluirLocal}>
                <span>
                  <Button
                    icon={<MinusCircleOutlined />}
                    size="small"
                    danger
                    onClick={() => excluirFaturaLocal(record)}
                    disabled={!podeExcluirLocal}
                    loading={acaoId === record.id}
                  />
                </span>
              </Tooltip>
            </Space>
          );
        },
      },
    ];

  const parcelaColumns = useMemo(
    () => [
      {
        title: 'Descrição',
        dataIndex: 'descricao_exibicao',
        key: 'descricao',
        render: (_, record) => getDescricaoParcelaExibicao(record),
      },
      {
        title: 'Valor',
        dataIndex: 'valor',
        key: 'valor',
        width: 160,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Vencimento',
        dataIndex: 'data_vencimento',
        key: 'data_vencimento',
        width: 140,
        render: (value) => formatDate(value),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status) => status || '-',
      },
    ],
    []
  );

  return (
    <div>
      {!isGestaoPage ? (
      <Card title="Operacional por Competência" style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <DatePicker
            picker="month"
            format="MM/YYYY"
            value={competenciaOperacional}
            onChange={(value) => setCompetenciaOperacional(value)}
          />
          <Tooltip title="Sincronizar lista operacional">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchOperacional(competenciaOperacional)}
              loading={operacionalLoading}
            />
          </Tooltip>
          <Segmented
            options={[
              { label: 'Seleção: Geração em lote', value: MODO_SELECAO.GERACAO },
              { label: 'Seleção: Envio ao ASAAS', value: MODO_SELECAO.ENVIO_ASAAS },
            ]}
            value={modoSelecaoLote}
            onChange={(value) => {
              setModoSelecaoLote(value);
              setSelectedOperacionalRowKeys([]);
              setSelectedOperacionalRows([]);
            }}
          />
          <Tooltip title={`Gerar faturas em lote (${selectedElegiveisGeracao} selecionadas elegíveis)`}>
            <span>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onGerarLote}
                loading={gerandoLote}
                disabled={selectedElegiveisGeracao === 0}
              />
            </span>
          </Tooltip>
          <Tooltip title={`Enviar ao Asaas em lote (${selectedElegiveisEnvioAsaas} selecionadas elegíveis)`}>
            <span>
              <Button
                icon={<SendOutlined />}
                onClick={onEnviarAsaasLote}
                loading={enviandoLoteAsaas}
                disabled={selectedElegiveisEnvioAsaas === 0}
              />
            </span>
          </Tooltip>
          <Text type="secondary">Geração elegível: {selectedElegiveisGeracao}</Text>
          <Text type="secondary">Envio elegível: {selectedElegiveisEnvioAsaas}</Text>
        </Space>

        <Table
          rowKey={(row) => getOperacionalRowKey(row)}
          loading={operacionalLoading}
          dataSource={operacionalRows}
          columns={operacionalColumns}
          rowSelection={operacionalRowSelection}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>
      ) : null}

      {!isOperacionalPage ? (
      <Card title="Alertas Operacionais" style={{ marginBottom: 16 }}>
        {possuiFiltroAlertaAtivo ? (
          <Space style={{ marginBottom: 8 }}>
            <Text type="secondary">Filtro de alerta ativo: {filtroAlertaAtivoLabel}</Text>
            <Button size="small" onClick={limparFiltrosAlerta}>
              Limpar filtros de alerta
            </Button>
          </Space>
        ) : null}
        <Space wrap>
          <Alert
            type="info"
            showIcon
            style={{ cursor: 'pointer' }}
            onClick={filtrarPertoVencimento}
            message={`Perto do vencimento (1 a 5 dias): ${alertasOperacionais.perto_vencimento}`}
            description="Clique para filtrar somente faturas perto do vencimento"
          />
          <Alert
            type="warning"
            showIcon
            style={{ cursor: 'pointer' }}
            onClick={filtrarVencemHoje}
            message={`Vencem hoje: ${alertasOperacionais.vence_hoje}`}
            description="Clique para filtrar somente faturas que vencem hoje"
          />
          <Alert
            type="error"
            showIcon
            style={{ cursor: 'pointer' }}
            onClick={filtrarSomenteVencidasPendentes}
            message={`Vencidas pendentes: ${alertasOperacionais.vencidas}`}
            description="Clique para filtrar somente vencidas pendentes"
          />
        </Space>
      </Card>
      ) : null}

      {!isOperacionalPage ? (
      <Card title="Filtros de Faturas" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Buscar aluno (texto livre)"
            style={{ width: 260 }}
            value={filtroAlunoInput}
            onChange={(event) => setFiltroAlunoInput(event?.target?.value || '')}
            onSearch={(value) => {
              const termo = (value || '').trim();
              setFiltroAluno(termo || undefined);
              setFiltroAlunoInput(termo);
              fetchFaturas(1, gestaoPageSize, { filtro_aluno: termo || undefined });
            }}
          />
          <Input.Search
            allowClear
            placeholder="Buscar responsável (texto livre)"
            style={{ width: 260 }}
            value={filtroResponsavelInput}
            onChange={(event) => setFiltroResponsavelInput(event?.target?.value || '')}
            onSearch={(value) => {
              const termo = (value || '').trim();
              setFiltroResponsavel(termo || undefined);
              setFiltroResponsavelInput(termo);
              fetchFaturas(1, gestaoPageSize, { filtro_responsavel: termo || undefined });
            }}
          />
          <Select
            allowClear
            placeholder="Filtrar por estado fatura"
            options={ESTADO_FATURA_OPTIONS}
            style={{ width: 220 }}
            value={filtroEstadoFatura}
            onChange={(value) => {
              setFiltroEstadoFatura(value);
              if (value) {
                setFiltroPertoVencimento(false);
                setFiltroVenceHoje(false);
                setFiltroVencidasPendentes(false);
              }
              fetchFaturas(1, gestaoPageSize, {
                filtro_estado_fatura: value,
                perto_vencimento: value ? false : filtroPertoVencimento,
                vence_hoje: value ? false : filtroVenceHoje,
                vencidas_pendentes: value ? false : filtroVencidasPendentes,
              });
            }}
          />
          <DatePicker.RangePicker
            style={{ width: 300 }}
            format="DD/MM/YYYY"
            value={filtroVencimentoRange}
            onChange={(values) => {
              const range = values && values.length === 2 ? values : null;
              setFiltroVencimentoRange(range);
              fetchFaturas(1, gestaoPageSize, {
                data_vencimento_inicio: range?.[0]?.format?.('YYYY-MM-DD') || undefined,
                data_vencimento_fim: range?.[1]?.format?.('YYYY-MM-DD') || undefined,
              });
            }}
          />
        </Space>
      </Card>
      ) : null}

      {!isOperacionalPage ? (
      <Card title="Faturas">
        <Space style={{ marginBottom: 12 }}>
          <Button
            icon={<SyncOutlined />}
            onClick={onAtualizarStatusAsaasSelecionadas}
            loading={atualizandoStatusLote}
            disabled={selectedFaturasRowKeys.length === 0}
          >
            Atualizar status ASAAS (selecionadas)
          </Button>
          <Text type="secondary">Selecionadas: {selectedFaturasRowKeys.length}</Text>
        </Space>
        {faturasSemCobranca > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`⚠️ Fatura sem cobrança no ASAAS: ${faturasSemCobranca}`}
          />
        ) : null}
        <Table
          rowKey="id"
          loading={loading}
          dataSource={faturas}
          columns={columns}
          rowSelection={faturasRowSelection}
          onChange={(pagination, _filters, sorter) => {
            const firstSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            let sortBy = DEFAULT_GESTAO_SORT_BY;
            if (firstSorter?.columnKey === 'aluno' || firstSorter?.field === 'nome_aluno') {
              sortBy = 'nome_aluno';
            } else if (firstSorter?.columnKey === 'vencimento' || firstSorter?.field === 'data_vencimento') {
              sortBy = 'data_vencimento';
            } else if (firstSorter?.columnKey === 'estado_fatura' || firstSorter?.field === 'estado_fatura') {
              sortBy = 'estado_fatura';
            } else if (firstSorter?.columnKey === 'status_asaas' || firstSorter?.field === 'status_asaas') {
              sortBy = 'status_asaas';
            }
            const sortOrder = firstSorter?.order === 'descend' ? 'desc' : 'asc';

            fetchFaturas(
              pagination?.current || gestaoPage,
              pagination?.pageSize || gestaoPageSize,
              {},
              { sort_by: sortBy, sort_order: sortOrder }
            );
          }}
          pagination={{
            current: gestaoPage,
            pageSize: gestaoPageSize,
            total: gestaoTotal,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total}`,
          }}
        />
      </Card>
      ) : null}

      {!isOperacionalPage ? (
      <Modal
        title="Detalhes da Fatura"
        open={detalheOpen}
        onCancel={() => setDetalheOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetalheOpen(false)}>
            Fechar
          </Button>,
        ]}
        width={900}
      >
        {detalheLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : detalheFatura ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="ID">{detalheFatura.id}</Descriptions.Item>
              <Descriptions.Item label="Nome do aluno">{detalheFatura.nome_aluno || '-'}</Descriptions.Item>
              <Descriptions.Item label="Competência">{detalheFatura.competencia || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status">{detalheFatura.status || '-'}</Descriptions.Item>
              <Descriptions.Item label="Valor total (sem desconto)">
                {formatCurrency(detalheTotais.valor_total_sem_desconto)}
              </Descriptions.Item>
              <Descriptions.Item label="Valor total (com desconto)">
                {formatCurrency(detalheTotais.valor_total_com_desconto)}
              </Descriptions.Item>
              <Descriptions.Item label="Vencimento">{formatDate(detalheFatura.data_vencimento)}</Descriptions.Item>
              <Descriptions.Item label="Descrição da cobrança" span={2}>
                <Text style={{ whiteSpace: 'pre-wrap' }}>
                  {montarDescricaoCobranca(detalheFatura)}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Card size="small" title="Parcelas da fatura">
              <Table
                size="small"
                rowKey={(row) => row.id}
                columns={parcelaColumns}
                dataSource={Array.isArray(detalheFatura.parcelas) ? detalheFatura.parcelas : []}
                pagination={false}
                locale={{ emptyText: 'Sem parcelas vinculadas' }}
              />
            </Card>
          </Space>
        ) : null}
      </Modal>
      ) : null}
    </div>
  );
};

export default FaturasList;
